import { POStatus } from '@prisma/client';
import { loadPersistentData, savePersistentData } from './fileStorage';
import { syncPOToDatabase } from './dbBridge';
import {
  prDatabase,
  auditLogsDatabase,
  addAuditLog,
  getPRAuditLogs,
  MASTER_ITEMS,
  PRRecord,
  PRItemRecord,
  PRAuditRecord,
} from './mockPrStore';

export interface SupplierSnapshot {
  id: string;
  code: string;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
  paymentTerms?: string;
}

export interface BankAccountSnapshot {
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  isPrimary?: boolean;
}

export interface POItemRecord {
  id: string;
  poId: string;
  prItemId: string; // Links to origin PR item
  prNumber: string; // Helper for UI display
  itemId: string;
  itemCode: string;
  itemName: string;
  orderedQty: number;
  receivedQty: number;
  unitPrice: number;
  subtotal: number;
  uomId: string;
  uomCode: string;
  notes?: string;
  initialOrderedQty?: number;
  releasedQty?: number;
  releaseReason?: string;
}

export interface PORecord {
  id: string;
  poNumber: string;
  date: string;
  supplierId: string;
  supplierSnapshot: SupplierSnapshot;
  bankAccountSnapshot: BankAccountSnapshot;
  purchaserId: string;
  purchaserName: string;
  status: POStatus;
  subtotal: number;
  tax: number;
  total: number;
  notes?: string;
  items: POItemRecord[];
  isRevision?: boolean;
  revisionNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export type POIssuedListener = (po: PORecord) => void;
export type POCancelListener = (poId: string, totalReceived: number) => void;
export type POCompletedListener = (poId: string) => void;

const onPOIssuedListeners: POIssuedListener[] = [];
const onPOCancelListeners: POCancelListener[] = [];
const onPOCompletedListeners: POCompletedListener[] = [];

export function registerPOIssuedListener(fn: POIssuedListener) {
  onPOIssuedListeners.push(fn);
}

export function registerPOCancelListener(fn: POCancelListener) {
  onPOCancelListeners.push(fn);
}

export function registerPOCompletedListener(fn: POCompletedListener) {
  onPOCompletedListeners.push(fn);
}

export interface SupplierMaster {
  id: string;
  code: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  paymentTerms: string;
  bankAccounts: BankAccountSnapshot[];
}

export const SUPPLIER_MASTER: SupplierMaster[] = [
  {
    id: 'sup-001',
    code: 'SUP-001',
    name: 'PT Packaging Jaya Abadi',
    phone: '+62-21-5551234',
    email: 'sales@packagingjaya.co.id',
    address: 'Kawasan Industri MM2100 Blok C, Cikarang Barat, Bekasi',
    paymentTerms: 'NET 30',
    bankAccounts: [
      {
        bankName: 'Bank Central Asia (BCA)',
        accountNumber: '8830192831',
        accountHolderName: 'PT Packaging Jaya Abadi',
        isPrimary: true,
      },
      {
        bankName: 'Bank Mandiri',
        accountNumber: '1560002938192',
        accountHolderName: 'PT Packaging Jaya Abadi',
        isPrimary: false,
      },
    ],
  },
  {
    id: 'sup-002',
    code: 'SUP-002',
    name: 'CV Sumber Logistik Mandiri',
    phone: '+62-21-5559876',
    email: 'order@sumberlogistik.co.id',
    address: 'Jl. Rawa Gelam IV No. 12, Kawasan Industri Pulogadung, Jakarta Timur',
    paymentTerms: 'COD',
    bankAccounts: [
      {
        bankName: 'Bank Mandiri',
        accountNumber: '1240008899123',
        accountHolderName: 'CV Sumber Logistik Mandiri',
        isPrimary: true,
      },
    ],
  },
  {
    id: 'sup-003',
    code: 'SUP-003',
    name: 'PT Mitra Sukses Industri',
    phone: '+62-21-7788990',
    email: 'info@mitrasukses.com',
    address: 'Kawasan Industri Jababeka II Blok J, Cikarang, Bekasi',
    paymentTerms: 'NET 14',
    bankAccounts: [
      {
        bankName: 'Bank Central Asia (BCA)',
        accountNumber: '7310892019',
        accountHolderName: 'PT Mitra Sukses Industri',
        isPrimary: true,
      },
    ],
  },
];

export function getAllSuppliersList(): SupplierMaster[] {
  const persisted = loadPersistentData<SupplierMaster[]>('supplier-database.json', SUPPLIER_MASTER);
  for (const s of SUPPLIER_MASTER) {
    if (!persisted.some((p) => p.id === s.id || p.code === s.code)) {
      persisted.push(s);
    }
  }
  return persisted;
}

// Initial PO dataset
const initialPoDatabase: PORecord[] = [
  {
    id: 'po-001',
    poNumber: 'PO-2026-09-001',
    date: '2026-09-14T10:00:00.000Z',
    supplierId: 'sup-001',
    supplierSnapshot: {
      id: 'sup-001',
      code: 'SUP-001',
      name: 'PT Packaging Jaya Abadi',
      phone: '+62-21-5551234',
      email: 'sales@packagingjaya.co.id',
      address: 'Kawasan Industri MM2100 Blok C, Cikarang Barat, Bekasi',
      paymentTerms: 'NET 30',
    },
    bankAccountSnapshot: {
      bankName: 'Bank Central Asia (BCA)',
      accountNumber: '8830192831',
      accountHolderName: 'PT Packaging Jaya Abadi',
      isPrimary: true,
    },
    purchaserId: 'usr-purchaser-01',
    purchaserName: 'Siti Rahma',
    status: POStatus.ISSUED,
    subtotal: 7360000,
    tax: 809600,
    total: 8169600,
    notes: 'Consolidated packaging orders for manufacturing line A',
    items: [
      {
        id: 'poi-001-1',
        poId: 'po-001',
        prItemId: 'pri-002-1',
        prNumber: 'PR-2026-09-002',
        itemId: 'itm-003',
        itemCode: 'ITM-003',
        itemName: 'Stretch Film 500mm x 250m',
        orderedQty: 80,
        receivedQty: 0,
        unitPrice: 92000,
        subtotal: 7360000,
        uomId: 'uom-002',
        uomCode: 'ROLL',
      },
    ],
    createdAt: '2026-09-14T10:00:00.000Z',
    updatedAt: '2026-09-14T10:30:00.000Z',
  },
  {
    id: 'po-002',
    poNumber: 'PO-2026-09-002',
    date: '2026-09-11T14:00:00.000Z',
    supplierId: 'sup-002',
    supplierSnapshot: {
      id: 'sup-002',
      code: 'SUP-002',
      name: 'CV Sumber Logistik Mandiri',
      phone: '+62-21-5559876',
      email: 'order@sumberlogistik.co.id',
      address: 'Jl. Rawa Gelam IV No. 12, Kawasan Industri Pulogadung, Jakarta Timur',
      paymentTerms: 'COD',
    },
    bankAccountSnapshot: {
      bankName: 'Bank Mandiri',
      accountNumber: '1240008899123',
      accountHolderName: 'CV Sumber Logistik Mandiri',
      isPrimary: true,
    },
    purchaserId: 'usr-purchaser-01',
    purchaserName: 'Siti Rahma',
    status: POStatus.PARTIALLY_RECEIVED,
    subtotal: 10250000,
    tax: 1127500,
    total: 11377500,
    notes: 'Pallets & tape shipment',
    items: [
      {
        id: 'poi-002-1',
        poId: 'po-002',
        prItemId: 'pri-003-1',
        prNumber: 'PR-2026-09-003',
        itemId: 'itm-005',
        itemCode: 'ITM-005',
        itemName: 'Standard Wooden Pallet 1200x1000mm',
        orderedQty: 40,
        receivedQty: 20,
        unitPrice: 225000,
        subtotal: 9000000,
        uomId: 'uom-001',
        uomCode: 'PCS',
      },
      {
        id: 'poi-002-2',
        poId: 'po-002',
        prItemId: 'pri-003-2',
        prNumber: 'PR-2026-09-003',
        itemId: 'itm-004',
        itemCode: 'ITM-004',
        itemName: 'Industrial Opp Tape 2 inch Clear',
        orderedQty: 100,
        receivedQty: 100,
        unitPrice: 12500,
        subtotal: 1250000,
        uomId: 'uom-001',
        uomCode: 'PCS',
      },
    ],
    createdAt: '2026-09-11T14:00:00.000Z',
    updatedAt: '2026-09-13T16:00:00.000Z',
  },
  {
    id: 'po-003',
    poNumber: 'PO-2026-09-003',
    date: '2026-09-14T09:00:00.000Z',
    supplierId: 'sup-003',
    supplierSnapshot: {
      id: 'sup-003',
      code: 'SUP-003',
      name: 'PT Mitra Sukses Industri',
      phone: '+62-21-7788990',
      email: 'info@mitrasukses.com',
      address: 'Kawasan Industri Jababeka II Blok J, Cikarang, Bekasi',
      paymentTerms: 'NET 14',
    },
    bankAccountSnapshot: {
      bankName: 'Bank Central Asia (BCA)',
      accountNumber: '7310892019',
      accountHolderName: 'PT Mitra Sukses Industri',
      isPrimary: true,
    },
    purchaserId: 'usr-purchaser-01',
    purchaserName: 'Siti Rahma',
    status: POStatus.COMPLETED,
    subtotal: 39570000,
    tax: 4352700,
    total: 43922700,
    notes: 'Pengadaan bahan baku denim & aksesoris jaket - Terpenuhi 100%',
    items: [
      {
        id: 'poi-003-1',
        poId: 'po-003',
        prItemId: 'pri-008-1',
        prNumber: 'PR-2026-09-008',
        itemId: 'itm-011',
        itemCode: 'ITM-011',
        itemName: 'Kain Denim Stretch 12oz Indigo',
        orderedQty: 800,
        receivedQty: 800,
        unitPrice: 45000,
        subtotal: 36000000,
        uomId: 'uom-003',
        uomCode: 'METER',
      },
      {
        id: 'poi-003-2',
        poId: 'po-003',
        prItemId: 'pri-008-2',
        prNumber: 'PR-2026-09-008',
        itemId: 'itm-010',
        itemCode: 'ITM-010',
        itemName: 'Resleting / Zipper YKK Metal 5 inch',
        orderedQty: 600,
        receivedQty: 600,
        unitPrice: 4200,
        subtotal: 2520000,
        uomId: 'uom-001',
        uomCode: 'PCS',
      },
      {
        id: 'poi-003-3',
        poId: 'po-003',
        prItemId: 'pri-005-4',
        prNumber: 'PR-2026-09-005',
        itemId: 'itm-014',
        itemCode: 'ITM-014',
        itemName: 'Label Woven Satin Brand PT XYZ',
        orderedQty: 3000,
        receivedQty: 3000,
        unitPrice: 350,
        subtotal: 1050000,
        uomId: 'uom-001',
        uomCode: 'PCS',
      },
    ],
    createdAt: '2026-09-14T09:00:00.000Z',
    updatedAt: '2026-09-15T11:00:00.000Z',
  },

  {
    id: 'po-005',
    poNumber: 'PO-2026-09-005',
    date: '2026-09-15T14:30:00.000Z',
    supplierId: 'sup-001',
    supplierSnapshot: {
      id: 'sup-001',
      code: 'SUP-001',
      name: 'PT Packaging Jaya Abadi',
      phone: '+62-21-5551234',
      email: 'sales@packagingjaya.co.id',
      address: 'Kawasan Industri MM2100 Blok C, Cikarang Barat, Bekasi',
      paymentTerms: 'NET 30',
    },
    bankAccountSnapshot: {
      bankName: 'Bank Central Asia (BCA)',
      accountNumber: '8830192831',
      accountHolderName: 'PT Packaging Jaya Abadi',
      isPrimary: true,
    },
    purchaserId: 'usr-purchaser-01',
    purchaserName: 'Siti Rahma',
    status: POStatus.ISSUED,
    subtotal: 7140000,
    tax: 785400,
    total: 7925400,
    notes: 'Kemasan polybag dan label barcode pengiriman ekspor Q4 - Menunggu kiriman vendor',
    items: [
      {
        id: 'poi-005-1',
        poId: 'po-005',
        prItemId: 'pri-006-2',
        prNumber: 'PR-2026-09-006',
        itemId: 'itm-013',
        itemCode: 'ITM-013',
        itemName: 'Plastik Polybag Klip 30x40cm',
        orderedQty: 5000,
        receivedQty: 0,
        unitPrice: 650,
        subtotal: 3250000,
        uomId: 'uom-001',
        uomCode: 'PCS',
      },
      {
        id: 'poi-005-2',
        poId: 'po-005',
        prItemId: 'pri-006-5',
        prNumber: 'PR-2026-09-006',
        itemId: 'itm-015',
        itemCode: 'ITM-015',
        itemName: 'Silica Gel Desiccant 2gr White',
        orderedQty: 80,
        receivedQty: 0,
        unitPrice: 25000,
        subtotal: 2000000,
        uomId: 'uom-004',
        uomCode: 'PACK',
      },
      {
        id: 'poi-005-3',
        poId: 'po-005',
        prItemId: 'pri-006-6',
        prNumber: 'PR-2026-09-006',
        itemId: 'itm-020',
        itemCode: 'ITM-020',
        itemName: 'Stiker Barcode Thermal 100x50mm',
        orderedQty: 45,
        receivedQty: 0,
        unitPrice: 42000,
        subtotal: 1890000,
        uomId: 'uom-002',
        uomCode: 'ROLL',
      },
    ],
    createdAt: '2026-09-15T14:30:00.000Z',
    updatedAt: '2026-09-15T14:30:00.000Z',
  },
];

export const poDatabase: PORecord[] = loadPersistentData('po-database.json', initialPoDatabase);
// pri-002-1: allocated 80 to po-001
const p2Item = prDatabase.flatMap((p) => p.items).find((i) => i.id === 'pri-002-1');
if (p2Item) {
  p2Item.allocatedQty = 80;
}

// Reflect allocations for po-003, po-004, and po-005
const pri008_1 = prDatabase.flatMap((p) => p.items).find((i) => i.id === 'pri-008-1');
if (pri008_1) { pri008_1.approvedQty = 800; pri008_1.allocatedQty = 800; pri008_1.status = 'APPROVED' as any; }
const pri008_2 = prDatabase.flatMap((p) => p.items).find((i) => i.id === 'pri-008-2');
if (pri008_2) { pri008_2.approvedQty = 600; pri008_2.allocatedQty = 600; pri008_2.status = 'APPROVED' as any; }
const pri005_4 = prDatabase.flatMap((p) => p.items).find((i) => i.id === 'pri-005-4');
if (pri005_4) { pri005_4.approvedQty = 3000; pri005_4.allocatedQty = 3000; pri005_4.status = 'APPROVED' as any; }



const pri006_2 = prDatabase.flatMap((p) => p.items).find((i) => i.id === 'pri-006-2');
if (pri006_2) { pri006_2.approvedQty = 5000; pri006_2.allocatedQty = 5000; pri006_2.status = 'APPROVED' as any; }
const pri006_5 = prDatabase.flatMap((p) => p.items).find((i) => i.id === 'pri-006-5');
if (pri006_5) { pri006_5.approvedQty = 80; pri006_5.allocatedQty = 80; pri006_5.status = 'APPROVED' as any; }
const pri006_6 = prDatabase.flatMap((p) => p.items).find((i) => i.id === 'pri-006-6');
if (pri006_6) { pri006_6.approvedQty = 45; pri006_6.allocatedQty = 45; pri006_6.status = 'APPROVED' as any; }

export function getPOs(statusFilter?: string, search?: string): PORecord[] {
  let list = [...poDatabase];
  if (statusFilter && statusFilter !== 'ALL') {
    list = list.filter((p) => p.status === statusFilter);
  }
  if (search) {
    const s = search.toLowerCase();
    list = list.filter(
      (p) =>
        p.poNumber.toLowerCase().includes(s) ||
        p.supplierSnapshot.name.toLowerCase().includes(s) ||
        p.supplierSnapshot.code.toLowerCase().includes(s) ||
        p.purchaserName.toLowerCase().includes(s) ||
        (p.notes && p.notes.toLowerCase().includes(s))
    );
  }
  return list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

export function getPOById(id: string): PORecord | undefined {
  return poDatabase.find((p) => p.id === id || p.poNumber === id);
}

export function generatePONumber(): string {
  const dateStr = new Date().toISOString().slice(0, 7).replace('-', '');
  const count = poDatabase.length + 1;
  return `PO-${dateStr}-${String(count).padStart(3, '0')}`;
}

export interface ApprovedPRItemCandidate {
  prItemId: string;
  prId: string;
  prNumber: string;
  prDate: string;
  department: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  category: string;
  requestedQty: number;
  approvedQty: number;
  allocatedQty: number;
  remainingQty: number;
  uomId: string;
  uomCode: string;
  estimatedPrice: number;
  notes?: string;
}

/**
 * Returns approved PR items that have allocatable quantity: (approvedQty - allocatedQty > 0)
 */
export function getApprovedPRItemsForAllocation(): ApprovedPRItemCandidate[] {
  const candidates: ApprovedPRItemCandidate[] = [];

  for (const pr of prDatabase) {
    // Sacred Invariant: Only PRs that have reached APPROVED status can have items converted to PO
    if (pr.status !== 'APPROVED') {
      continue;
    }

    for (const item of pr.items) {
      if (item.status === 'APPROVED') {
        const remaining = Number((item.approvedQty - item.allocatedQty).toFixed(4));
        if (remaining > 0) {
          candidates.push({
            prItemId: item.id,
            prId: pr.id,
            prNumber: pr.prNumber,
            prDate: pr.date,
            department: pr.department,
            itemId: item.itemId,
            itemCode: item.itemCode,
            itemName: item.itemName,
            category: item.category,
            requestedQty: item.requestedQty,
            approvedQty: item.approvedQty,
            allocatedQty: item.allocatedQty,
            remainingQty: remaining,
            uomId: item.uomId,
            uomCode: item.uomCode,
            estimatedPrice: item.estimatedPrice,
            notes: item.notes,
          });
        }
      }
    }
  }

  return candidates;
}

export interface CreatePOItemInput {
  prItemId: string;
  itemId: string;
  orderedQty: number;
  unitPrice: number;
  notes?: string;
}

export interface CreatePOPayload {
  supplierId: string;
  bankAccount: BankAccountSnapshot;
  items: CreatePOItemInput[];
  notes?: string;
  taxRate?: number; // default 0.11
  issueImmediately?: boolean;
}

/**
 * Creates a PO from approved PR items, snapshotting the supplier & bank account,
 * and atomically updating allocatedQty on each PR item.
 */
let poIdCounter = 1;

export function createPurchaseOrder(
  payload: CreatePOPayload,
  purchaser: { id: string; name: string; role: string }
): PORecord {
  const suppliersList = getAllSuppliersList();
  const q = (payload.supplierId || '').toLowerCase().trim();
  const supplier = suppliersList.find(
    (s) => s.id.toLowerCase() === q || s.code.toLowerCase() === q
  );
  if (!supplier) {
    throw new Error(`Supplier '${payload.supplierId}' not found.`);
  }

  if (!payload.bankAccount || !payload.bankAccount.bankName || !payload.bankAccount.accountNumber) {
    throw new Error('A valid bank account snapshot is required.');
  }

  if (!Array.isArray(payload.items) || payload.items.length === 0) {
    throw new Error('At least one line item is required to create a Purchase Order.');
  }

  // Pre-validate all items before mutating state
  const validatedItems: Array<{
    prItem: PRItemRecord;
    prNumber: string;
    catalogItem: (typeof MASTER_ITEMS)[0];
    orderedQty: number;
    unitPrice: number;
    notes?: string;
  }> = [];

  for (let idx = 0; idx < payload.items.length; idx++) {
    const input = payload.items[idx];
    const orderedQty = Number(input.orderedQty);
    const unitPrice = Number(input.unitPrice);

    if (isNaN(orderedQty) || orderedQty <= 0) {
      throw new Error(`Item #${idx + 1}: Ordered quantity must be strictly greater than 0.`);
    }
    if (isNaN(unitPrice) || unitPrice < 0) {
      throw new Error(`Item #${idx + 1}: Unit price must be greater than or equal to 0.`);
    }

    // Locate PR item & parent PR
    let foundPRItem: PRItemRecord | undefined;
    let foundPR: PRRecord | undefined;

    for (const pr of prDatabase) {
      const match = pr.items.find((i) => i.id === input.prItemId);
      if (match) {
        foundPRItem = match;
        foundPR = pr;
        break;
      }
    }

    if (!foundPRItem || !foundPR) {
      throw new Error(`Origin PR Item '${input.prItemId}' not found.`);
    }

    // Sacred Invariant: PR must be APPROVED before any of its items can become a PO
    if (foundPR.status !== 'APPROVED') {
      throw new Error(
        `Purchase Request '${foundPR.prNumber}' is not APPROVED (current status: ${foundPR.status}). Only items from an APPROVED PR can be converted to a Purchase Order.`
      );
    }

    // Sacred Invariant: PR Item must be APPROVED
    if (foundPRItem.status !== 'APPROVED') {
      throw new Error(
        `PR Item '${foundPRItem.itemName}' (${foundPRItem.id}) is not APPROVED (status: ${foundPRItem.status}). Rejected or pending PR items cannot be converted to PO.`
      );
    }

    const remaining = Number((foundPRItem.approvedQty - foundPRItem.allocatedQty).toFixed(4));
    if (orderedQty > remaining) {
      throw new Error(
        `Cannot allocate ${orderedQty} for '${foundPRItem.itemName}'. Available unallocated approved quantity is ${remaining}.`
      );
    }

    const catalogItem = MASTER_ITEMS.find((m) => m.id === input.itemId || m.id === foundPRItem?.itemId) || {
      id: input.itemId,
      code: foundPRItem.itemCode,
      name: foundPRItem.itemName,
      category: foundPRItem.category,
      specs: '',
      defaultUomId: foundPRItem.uomId,
      uomCode: foundPRItem.uomCode,
      defaultPrice: unitPrice,
    };

    validatedItems.push({
      prItem: foundPRItem,
      prNumber: foundPR.prNumber,
      catalogItem,
      orderedQty,
      unitPrice,
      notes: input.notes,
    });
  }

  // Atomically allocate PR quantities
  for (const item of validatedItems) {
    item.prItem.allocatedQty = Number((item.prItem.allocatedQty + item.orderedQty).toFixed(4));
  }

  const initialStatus = payload.issueImmediately ? POStatus.ISSUED : POStatus.DRAFT;
  // Official PO number is assigned ONLY when submitted/issued; draft POs do not have an official PO number
  const poNumber = initialStatus === POStatus.ISSUED ? generatePONumber() : '[Draf Belum Terbit]';
  const id = `po-${Date.now()}-${poIdCounter++}`;
  const date = new Date().toISOString();

  // Monetary precision with Decimal(18, 4) simulated in deterministic 4-decimal precision
  const taxRate = payload.taxRate !== undefined ? payload.taxRate : 0.11;
  const poItems: POItemRecord[] = validatedItems.map((vi, idx) => {
    const subtotal = Number((vi.orderedQty * vi.unitPrice).toFixed(4));
    return {
      id: `poi-${id}-${idx + 1}`,
      poId: id,
      prItemId: vi.prItem.id,
      prNumber: vi.prNumber,
      itemId: vi.catalogItem.id,
      itemCode: vi.catalogItem.code,
      itemName: vi.catalogItem.name,
      orderedQty: vi.orderedQty,
      receivedQty: 0,
      unitPrice: vi.unitPrice,
      subtotal,
      uomId: vi.prItem.uomId,
      uomCode: vi.prItem.uomCode,
      notes: vi.notes,
    };
  });

  const subtotal = Number(poItems.reduce((sum, it) => sum + it.subtotal, 0).toFixed(4));
  const tax = Number((subtotal * taxRate).toFixed(4));
  const total = Number((subtotal + tax).toFixed(4));

  const newPO: PORecord = {
    id,
    poNumber,
    date,
    supplierId: supplier.id,
    supplierSnapshot: {
      id: supplier.id,
      code: supplier.code,
      name: supplier.name,
      phone: supplier.phone,
      email: supplier.email,
      address: supplier.address,
      paymentTerms: supplier.paymentTerms,
    },
    bankAccountSnapshot: {
      bankName: payload.bankAccount.bankName,
      accountNumber: payload.bankAccount.accountNumber,
      accountHolderName: payload.bankAccount.accountHolderName,
      isPrimary: payload.bankAccount.isPrimary,
    },
    purchaserId: purchaser.id,
    purchaserName: purchaser.name,
    status: initialStatus,
    subtotal,
    tax,
    total,
    notes: payload.notes,
    items: poItems,
    createdAt: date,
    updatedAt: date,
  };

  poDatabase.push(newPO);
  savePersistentData('po-database.json', poDatabase);
  savePersistentData('pr-database.json', prDatabase);
  syncPOToDatabase(newPO);

  if (payload.issueImmediately) {
    onPOIssuedListeners.forEach((fn) => fn(newPO));
  }

  // Record audit log
  addAuditLog({
    entityType: 'PURCHASE_ORDER',
    entityId: id,
    fromStatus: null,
    toStatus: initialStatus,
    actorId: purchaser.id,
    actorName: purchaser.name,
    actorRole: purchaser.role,
    action: payload.issueImmediately ? 'CREATE_AND_ISSUE_PO' : 'CREATE_DRAFT_PO',
    reason: payload.issueImmediately
      ? `Purchase Order ${poNumber} resmi diterbitkan langsung ke supplier ${supplier.name} merangkum ${poItems.length} item material PR.`
      : `Draf Purchase Order dibuat untuk supplier ${supplier.name}, merangkum ${poItems.length} item material PR. Rekening pembayaran telah diverifikasi.`,
    metadata: {
      poNumber,
      supplierCode: supplier.code,
      total,
      bankAccount: payload.bankAccount,
    },
  });

  return newPO;
}

/**
 * Transitions PO from DRAFT to ISSUED.
 */
export function issuePurchaseOrder(
  poId: string,
  actor: { id: string; name: string; role: string }
): PORecord {
  const po = poDatabase.find((p) => p.id === poId || p.poNumber === poId);
  if (!po) {
    throw new Error(`PO '${poId}' not found.`);
  }

  if (po.status !== POStatus.DRAFT) {
    throw new Error(`Cannot issue PO with status '${po.status}'. Only DRAFT POs can be issued.`);
  }

  // Assign official PO number if draft did not have one yet
  // If it already had an official PO number (e.g. submitted then returned to draft for revision), retain it!
  if (!po.poNumber || po.poNumber === '[Draf Belum Terbit]' || po.poNumber.startsWith('DRAF') || po.poNumber === '-') {
    po.poNumber = generatePONumber();
  }

  po.status = POStatus.ISSUED;
  po.isRevision = false;
  po.updatedAt = new Date().toISOString();
  savePersistentData('po-database.json', poDatabase);
  syncPOToDatabase(po);
  onPOIssuedListeners.forEach((fn) => fn(po));

  addAuditLog({
    entityType: 'PURCHASE_ORDER',
    entityId: po.id,
    fromStatus: POStatus.DRAFT,
    toStatus: POStatus.ISSUED,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'ISSUE_PO',
    reason: `Purchase Order resmi diterbitkan dan dikirimkan kepada supplier ${po.supplierSnapshot.name}. Nilai komitmen dan rekening pembayaran resmi telah dikunci.`,
    metadata: {
      poNumber: po.poNumber,
      supplierSnapshot: po.supplierSnapshot,
      bankAccountSnapshot: po.bankAccountSnapshot,
    },
  });

  return po;
}

/**
 * Cancels a PO. Strictly checks that no receiving has occurred (receivedQty == 0 on all items).
 * Atomically returns allocated quantities back to PR items.
 */
export function cancelPurchaseOrder(
  poId: string,
  actor: { id: string; name: string; role: string },
  reason?: string
): PORecord {
  const po = poDatabase.find((p) => p.id === poId || p.poNumber === poId);
  if (!po) {
    throw new Error(`PO '${poId}' not found.`);
  }

  if (po.status === POStatus.CANCELLED) {
    throw new Error(`PO '${po.poNumber}' is already cancelled.`);
  }

  if (po.status === POStatus.COMPLETED) {
    throw new Error(`Cannot cancel a COMPLETED PO.`);
  }

  // Safety check: Cannot cancel if goods receipt has commenced
  const totalReceived = po.items.reduce((sum, it) => sum + (it.receivedQty || 0), 0);
  if (totalReceived > 0) {
    throw new Error(
      `Cannot cancel PO '${po.poNumber}': Goods receipt has already commenced (${totalReceived} items received). Partial or full receipts prohibit cancellation.`
    );
  }

  const previousStatus = po.status;
  po.status = POStatus.CANCELLED;
  po.updatedAt = new Date().toISOString();
  savePersistentData('po-database.json', poDatabase);
  savePersistentData('pr-database.json', prDatabase);
  syncPOToDatabase(po);
  onPOCancelListeners.forEach((fn) => fn(po.id, 0));

  // Atomically decrement allocatedQty on PR items
  for (const item of po.items) {
    if (item.prItemId) {
      for (const pr of prDatabase) {
        const prItem = pr.items.find((i) => i.id === item.prItemId);
        if (prItem) {
          const newAllocated = Math.max(0, Number((prItem.allocatedQty - item.orderedQty).toFixed(4)));
          prItem.allocatedQty = newAllocated;
          break;
        }
      }
    }
  }

  addAuditLog({
    entityType: 'PURCHASE_ORDER',
    entityId: po.id,
    fromStatus: previousStatus,
    toStatus: POStatus.CANCELLED,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'CANCEL_PO',
    reason: reason || 'Dokumen PO dibatalkan oleh purchasing. Seluruh alokasi kuantitas dikembalikan ke PR asal.',
    metadata: {
      poNumber: po.poNumber,
      deallocatedItemsCount: po.items.length,
    },
  });

  return po;
}

export interface ReleaseUnfulfilledItemInput {
  poItemId: string;
  releaseQty: number; // Quantity to release (must be > 0 and <= outstanding)
  reason: string;
}

/**
 * Releases unfulfillable quantity from a Purchase Order item.
 * Reduces the PO item's ordered quantity, recalculates PO totals,
 * and atomically decrements allocatedQty in the origin PR item so that
 * the released quantity becomes available again for new PO creation.
 */
export function releaseUnfulfilledPOItem(
  poId: string,
  input: ReleaseUnfulfilledItemInput,
  actor: { id: string; name: string; role: string }
): {
  po: PORecord;
  releasedQty: number;
  prNumber?: string;
  itemName: string;
} {
  const po = poDatabase.find((p) => p.id === poId || p.poNumber === poId);
  if (!po) {
    throw new Error(`PO '${poId}' tidak ditemukan.`);
  }

  if (po.status === POStatus.CANCELLED) {
    throw new Error(`PO '${po.poNumber}' sudah dibatalkan.`);
  }

  if (po.status === POStatus.COMPLETED) {
    throw new Error(`PO '${po.poNumber}' sudah selesai (COMPLETED). Tidak ada sisa item yang dapat dikeluarkan.`);
  }

  const item = po.items.find((it) => it.id === input.poItemId);
  if (!item) {
    throw new Error(`Item dengan ID '${input.poItemId}' tidak ditemukan pada PO '${po.poNumber}'.`);
  }

  const outstanding = Math.max(0, item.orderedQty - (item.receivedQty || 0));
  if (outstanding <= 0) {
    throw new Error(
      `Item '${item.itemName}' sudah diterima lengkap (${item.receivedQty} dari ${item.orderedQty}). Tidak ada sisa kuantitas untuk dikeluarkan.`
    );
  }

  if (input.releaseQty <= 0) {
    throw new Error('Kuantitas pelepasan harus lebih besar dari 0.');
  }

  if (input.releaseQty > outstanding) {
    throw new Error(
      `Kuantitas pelepasan (${input.releaseQty}) tidak boleh melebihi sisa yang belum diterima (${outstanding} ${item.uomCode}).`
    );
  }

  // 1. Update PO Item record
  if (item.initialOrderedQty === undefined) {
    item.initialOrderedQty = item.orderedQty;
  }
  item.releasedQty = (item.releasedQty || 0) + input.releaseQty;
  item.orderedQty = item.orderedQty - input.releaseQty;
  item.subtotal = Number((item.orderedQty * item.unitPrice).toFixed(2));
  item.releaseReason = input.reason;

  // 2. Recalculate PO totals
  const newSubtotal = po.items.reduce((sum, it) => sum + (it.subtotal || 0), 0);
  po.subtotal = newSubtotal;
  po.tax = Number((newSubtotal * 0.11).toFixed(2));
  po.total = Number((newSubtotal + po.tax).toFixed(2));
  po.updatedAt = new Date().toISOString();

  // 3. Atomically de-allocate from originating PR item
  let prNumberFound: string | undefined = item.prNumber;
  if (item.prItemId) {
    for (const pr of prDatabase) {
      const prItem = pr.items.find((i) => i.id === item.prItemId);
      if (prItem) {
        prNumberFound = pr.prNumber;
        const newAllocated = Math.max(0, Number((prItem.allocatedQty - input.releaseQty).toFixed(4)));
        prItem.allocatedQty = newAllocated;

        addAuditLog({
          entityType: 'PURCHASE_REQUEST',
          entityId: pr.id,
          fromStatus: pr.status,
          toStatus: pr.status,
          actorId: actor.id,
          actorName: actor.name,
          actorRole: actor.role,
          action: 'DEALLOCATE_UNFULFILLED_QTY',
          reason: `Kuantitas sebanyak ${input.releaseQty} ${item.uomCode} untuk '${item.itemName}' dikembalikan ke PR asal karena supplier pada PO ${po.poNumber} tidak dapat memenuhinya. Alasan: ${input.reason}`,
          metadata: {
            poId: po.id,
            poNumber: po.poNumber,
            prItemId: item.prItemId,
            releasedQty: input.releaseQty,
            newAllocatedQty: newAllocated,
          },
        });
        break;
      }
    }
  }

  // 4. Status Auto-Reconciliation for PO
  const remainingTotalOutstanding = po.items.reduce(
    (sum, it) => sum + Math.max(0, it.orderedQty - (it.receivedQty || 0)),
    0
  );
  const totalReceived = po.items.reduce((sum, it) => sum + (it.receivedQty || 0), 0);

  const previousStatus = po.status;
  if (remainingTotalOutstanding === 0) {
    if (totalReceived > 0) {
      po.status = POStatus.COMPLETED;
      onPOCompletedListeners.forEach((fn) => fn(po.id));
    } else {
      po.status = POStatus.CANCELLED;
      onPOCancelListeners.forEach((fn) => fn(po.id, 0));
    }
  }

  // 5. Add Audit Log to PO
  addAuditLog({
    entityType: 'PURCHASE_ORDER',
    entityId: po.id,
    fromStatus: previousStatus,
    toStatus: po.status,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'RELEASE_UNFULFILLED_ITEM',
    reason: `Mengeluarkan ${input.releaseQty} ${item.uomCode} item '${item.itemName}'. Alasan: ${input.reason}`,
    metadata: {
      poNumber: po.poNumber,
      poItemId: item.id,
      prItemId: item.prItemId,
      prNumber: prNumberFound,
      itemName: item.itemName,
      releasedQty: input.releaseQty,
      initialOrderedQty: item.initialOrderedQty,
      newOrderedQty: item.orderedQty,
      receivedQty: item.receivedQty,
      newPOStatus: po.status,
    },
  });

  savePersistentData('po-database.json', poDatabase);
  savePersistentData('pr-database.json', prDatabase);
  syncPOToDatabase(po);

  return {
    po,
    releasedQty: input.releaseQty,
    prNumber: prNumberFound,
    itemName: item.itemName,
  };
}

/**
 * Releases all remaining unfulfilled quantities on a PO (Short-Close PO).
 */
export function releaseAllUnfulfilledPOItems(
  poId: string,
  reason: string,
  actor: { id: string; name: string; role: string }
): {
  po: PORecord;
  releasedItemsCount: number;
  totalReleasedUnits: number;
} {
  const po = poDatabase.find((p) => p.id === poId || p.poNumber === poId);
  if (!po) {
    throw new Error(`PO '${poId}' tidak ditemukan.`);
  }

  let releasedItemsCount = 0;
  let totalReleasedUnits = 0;

  // Clone items array reference to iterate safely
  const itemsWithOutstanding = po.items
    .map((it) => ({ id: it.id, outstanding: Math.max(0, it.orderedQty - (it.receivedQty || 0)) }))
    .filter((it) => it.outstanding > 0);

  for (const it of itemsWithOutstanding) {
    releaseUnfulfilledPOItem(
      po.id,
      {
        poItemId: it.id,
        releaseQty: it.outstanding,
        reason,
      },
      actor
    );
    releasedItemsCount++;
    totalReleasedUnits += it.outstanding;
  }

  savePersistentData('po-database.json', poDatabase);
  savePersistentData('pr-database.json', prDatabase);
  syncPOToDatabase(po);

  const totalReceived = po.items.reduce((sum, it) => sum + (it.receivedQty || 0), 0);
  if (totalReceived > 0) {
    onPOCompletedListeners.forEach((fn) => fn(po.id));
  } else {
    onPOCancelListeners.forEach((fn) => fn(po.id, totalReceived));
  }

  return {
    po,
    releasedItemsCount,
    totalReleasedUnits,
  };
}

export function returnPOToDraftForRevision(
  poId: string,
  revisionNotes: string,
  actor: { id: string; name: string; role: string }
): PORecord {
  const po = poDatabase.find((p) => p.id === poId || p.poNumber === poId);
  if (!po) throw new Error(`PO '${poId}' tidak ditemukan.`);

  const totalReceived = po.items.reduce((sum, it) => sum + (it.receivedQty || 0), 0);
  if (totalReceived > 0) {
    throw new Error('PO yang sudah memiliki penerimaan barang (GR) tidak dapat dikembalikan ke status draf untuk revisi.');
  }

  const previousStatus = po.status;
  po.status = POStatus.DRAFT;
  po.isRevision = true;
  po.revisionNotes = revisionNotes;
  po.updatedAt = new Date().toISOString();
  savePersistentData('po-database.json', poDatabase);
  syncPOToDatabase(po);

  addAuditLog({
    entityType: 'PURCHASE_ORDER',
    entityId: po.id,
    fromStatus: previousStatus,
    toStatus: POStatus.DRAFT,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'REVISE_PO',
    reason: revisionNotes,
    metadata: {
      poNumber: po.poNumber,
      revisionNotes,
    },
  });

  return po;
}

export function getPOAuditLogs(poId: string): PRAuditRecord[] {
  const po = poDatabase.find((p) => p.id === poId || p.poNumber === poId);
  const targetIds = new Set([poId, po?.id, po?.poNumber].filter(Boolean) as string[]);

  let logs = auditLogsDatabase.filter(
    (log: PRAuditRecord) =>
      targetIds.has(log.entityId) ||
      (typeof log.metadata?.poNumber === 'string' && targetIds.has(log.metadata.poNumber))
  );

  // If no logs exist yet for this PO (e.g. seed POs), synthesize foundational lifecycle audit trail
  if (logs.length === 0 && po) {
    const syntheticLogs: PRAuditRecord[] = [];

    // 1. PO Creation / Issuance Log
    syntheticLogs.push({
      id: `aud-${po.id}-create`,
      entityType: 'PURCHASE_ORDER',
      entityId: po.id,
      fromStatus: null,
      toStatus: po.status === 'DRAFT' ? 'DRAFT' : 'ISSUED',
      actorId: po.purchaserId || 'usr-purchaser-01',
      actorName: po.purchaserName || 'Siti Rahma',
      actorRole: 'PURCHASER',
      action: po.status === 'DRAFT' ? 'CREATE_DRAFT_PO' : 'ISSUE_PO',
      reason: po.status === 'DRAFT'
        ? `Draf Purchase Order dibuat untuk ${po.supplierSnapshot?.name || 'supplier rekanan'}. Rekening pembayaran resmi telah diverifikasi.`
        : `Purchase Order resmi diterbitkan dan dikirimkan kepada ${po.supplierSnapshot?.name || 'supplier rekanan'}. Nilai kontrak Rp ${po.total?.toLocaleString('id-ID')} dan rekening pembayaran resmi telah dikunci.`,
      timestamp: po.createdAt || po.date,
    });

    // 2. Receiving Log if partially received or completed
    if (po.status === 'PARTIALLY_RECEIVED' || po.status === 'COMPLETED') {
      const totalRec = po.items?.reduce((s, it) => s + (it.receivedQty || 0), 0) || 0;
      syntheticLogs.push({
        id: `aud-${po.id}-receive`,
        entityType: 'PURCHASE_ORDER',
        entityId: po.id,
        fromStatus: 'ISSUED',
        toStatus: po.status,
        actorId: 'usr-wh-01',
        actorName: 'Agus Wibowo',
        actorRole: 'WAREHOUSE',
        action: po.status === 'COMPLETED' ? 'COMPLETE_PO' : 'RECEIVE_GOODS',
        reason: po.status === 'COMPLETED'
          ? `Seluruh item pesanan (${totalRec.toLocaleString('id-ID')} unit) telah selesai diterima lengkap di gudang dan tercatat di inventaris.`
          : `Penerimaan bertahap barang masuk gudang (${totalRec.toLocaleString('id-ID')} unit) berhasil dicatat dan diverifikasi.`,
        timestamp: po.updatedAt || po.date,
      });
    }

    // 3. Cancellation Log if cancelled
    if (po.status === 'CANCELLED') {
      syntheticLogs.push({
        id: `aud-${po.id}-cancel`,
        entityType: 'PURCHASE_ORDER',
        entityId: po.id,
        fromStatus: 'ISSUED',
        toStatus: 'CANCELLED',
        actorId: 'usr-owner-01',
        actorName: 'Budi Santoso',
        actorRole: 'OWNER',
        action: 'CANCEL_PO',
        reason: po.notes || 'Pembatalan dokumen Purchase Order atas keputusan manajemen.',
        timestamp: po.updatedAt || po.date,
      });
    }

    logs = syntheticLogs;
  }

  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
}

/**
 * Updates a DRAFT Purchase Order with purchasing assessment changes:
 * allows updating supplier assignment, beneficiary bank account, contract unit prices, and notes.
 */
export function updatePurchaseOrder(
  id: string,
  data: {
    supplierId?: string;
    bankAccount?: BankAccountSnapshot;
    notes?: string;
    items?: Array<{
      id: string; // item id or poi id
      unitPrice?: number;
      notes?: string;
    }>;
  },
  actor: { id: string; name: string; role: string }
): PORecord {
  const po = poDatabase.find((p) => p.id === id || p.poNumber === id);
  if (!po) throw new Error(`PO '${id}' tidak ditemukan.`);

  if (po.status !== 'DRAFT') {
    throw new Error(`Tidak dapat mengubah PO berstatus '${po.status}'. Hanya PO draf yang dapat diubah saat asesmen pengadaan.`);
  }

  // Update supplier if changed
  if (data.supplierId && data.supplierId !== po.supplierId) {
    const suppliersList = getAllSuppliersList();
    const sup = suppliersList.find((s) => s.id === data.supplierId || s.code === data.supplierId);
    if (!sup) throw new Error(`Supplier '${data.supplierId}' tidak ditemukan.`);
    po.supplierId = sup.id;
    po.supplierSnapshot = {
      id: sup.id,
      code: sup.code,
      name: sup.name,
      phone: sup.phone,
      email: sup.email,
      address: sup.address,
      paymentTerms: sup.paymentTerms,
    };
    if (data.bankAccount) {
      po.bankAccountSnapshot = data.bankAccount;
    } else {
      const primaryBank = sup.bankAccounts.find((b) => b.isPrimary) || sup.bankAccounts[0];
      po.bankAccountSnapshot = {
        bankName: primaryBank.bankName,
        accountNumber: primaryBank.accountNumber,
        accountHolderName: primaryBank.accountHolderName,
        isPrimary: true,
      };
    }
  } else if (data.bankAccount) {
    po.bankAccountSnapshot = data.bankAccount;
  }

  if (data.notes !== undefined) {
    po.notes = data.notes;
  }

  // Update item unit prices or notes
  if (Array.isArray(data.items)) {
    for (const updateIt of data.items) {
      const line = po.items.find((i) => i.id === updateIt.id || i.itemId === updateIt.id);
      if (line) {
        if (updateIt.unitPrice !== undefined && updateIt.unitPrice >= 0) {
          line.unitPrice = Number(updateIt.unitPrice);
          line.subtotal = Number((line.orderedQty * line.unitPrice).toFixed(4));
        }
        if (updateIt.notes !== undefined) {
          line.notes = updateIt.notes;
        }
      }
    }
    // Recalculate totals
    po.subtotal = Number(po.items.reduce((s, it) => s + it.subtotal, 0).toFixed(4));
    po.tax = Number((po.subtotal * 0.11).toFixed(4));
    po.total = Number((po.subtotal + po.tax).toFixed(4));
  }

  po.updatedAt = new Date().toISOString();

  addAuditLog({
    entityType: 'PURCHASE_ORDER',
    entityId: po.id,
    fromStatus: po.status,
    toStatus: po.status,
    actorId: actor.id,
    actorName: actor.name,
    actorRole: actor.role,
    action: 'UPDATE_PO_ASSESSMENT',
    reason: 'Asesmen Purchasing: penyesuaian supplier dan harga kontrak',
  });

  return po;
}

/**
 * Automatically generates and issues Purchase Order(s) from an approved PR.
 * Groups approved items by their designated/best-match supplier.
 */
export function autoCreatePOForApprovedPR(
  prId: string,
  actor: { id: string; name: string; role: string }
): PORecord[] {
  const pr = prDatabase.find((p) => p.id === prId || p.prNumber === prId);
  if (!pr) {
    throw new Error(`PR '${prId}' not found for auto PO creation.`);
  }

  // Find all approved items with allocatable quantity
  const allocatableItems = pr.items.filter(
    (item) => item.status === 'APPROVED' && item.approvedQty - item.allocatedQty > 0
  );

  if (allocatableItems.length === 0) {
    return [];
  }

  // Resolve best supplier and unit price for each item
  interface ItemWithSupplier {
    item: PRItemRecord;
    supplier: (typeof SUPPLIER_MASTER)[0];
    unitPrice: number;
  }

  const itemsWithSupplier: ItemWithSupplier[] = allocatableItems.map((item) => {
    // 1. Check if previous PO ordered this item
    let resolvedSupplier: (typeof SUPPLIER_MASTER)[0] | undefined;
    let unitPrice = item.estimatedPrice > 0 ? item.estimatedPrice : 0;

    for (const po of poDatabase) {
      const line = po.items.find((it) => it.itemId === item.itemId || it.itemCode === item.itemCode);
      if (line) {
        const suppliersList = getAllSuppliersList();
        resolvedSupplier = suppliersList.find((s) => s.id === po.supplierId);
        if (unitPrice === 0 && line.unitPrice > 0) {
          unitPrice = line.unitPrice;
        }
        if (resolvedSupplier) break;
      }
    }

    // 2. Category heuristics if not found in past POs
    if (!resolvedSupplier) {
      const cat = (item.category || '').toLowerCase();
      if (cat.includes('packaging')) {
        resolvedSupplier = SUPPLIER_MASTER.find((s) => s.id === 'sup-001') || SUPPLIER_MASTER[0];
      } else if (cat.includes('warehouse') || cat.includes('consumable')) {
        resolvedSupplier = SUPPLIER_MASTER.find((s) => s.id === 'sup-002') || SUPPLIER_MASTER[0];
      } else {
        resolvedSupplier = SUPPLIER_MASTER.find((s) => s.id === 'sup-003') || SUPPLIER_MASTER[0];
      }
    }

    // Fallback price from master items catalog
    if (unitPrice === 0) {
      const catItem = MASTER_ITEMS.find((m) => m.id === item.itemId || m.code === item.itemCode);
      if (catItem && catItem.defaultPrice) {
        unitPrice = catItem.defaultPrice;
      }
    }

    return {
      item,
      supplier: resolvedSupplier,
      unitPrice,
    };
  });

  // Group items by supplierId
  const supplierGroups = new Map<string, ItemWithSupplier[]>();
  for (const entry of itemsWithSupplier) {
    const sId = entry.supplier.id;
    const existing = supplierGroups.get(sId) || [];
    existing.push(entry);
    supplierGroups.set(sId, existing);
  }

  const createdPOs: PORecord[] = [];

  // Generate 1 PO per supplier group
  for (const [, groupItems] of supplierGroups.entries()) {
    const supplier = groupItems[0].supplier;
    const primaryBank = supplier.bankAccounts.find((b) => b.isPrimary) || supplier.bankAccounts[0];

    const poPayload: CreatePOPayload = {
      supplierId: supplier.id,
      bankAccount: {
        bankName: primaryBank.bankName,
        accountNumber: primaryBank.accountNumber,
        accountHolderName: primaryBank.accountHolderName,
        isPrimary: true,
      },
      notes: `Purchase Order otomatis terbit dari persetujuan PR #${pr.prNumber}`,
      items: groupItems.map((entry) => ({
        prItemId: entry.item.id,
        itemId: entry.item.itemId,
        orderedQty: entry.item.approvedQty - entry.item.allocatedQty,
        unitPrice: entry.unitPrice,
        notes: entry.item.notes,
      })),
    };

    const newPO = createPurchaseOrder(poPayload, actor);

    // Auto-issue the PO so it is immediately active and ready for receiving
    const issuedPO = issuePurchaseOrder(newPO.id, actor);
    createdPOs.push(issuedPO);
  }

  return createdPOs;
}
